import { Box } from "@chakra-ui/react";
import { colors, customEvent, Spacing } from "goi_common";
import { BaseStack } from "goi_common/src/design-system";
import { useEffect, useState } from "react";
import { MubinsoConversionScreen } from "./MubinsoConversionScreen";
import { MubinsoCostChart } from "./MubinsoCostChart";
import { MubinsoLeadModal } from "./MubinsoLeadModal";
import { MubinsoCtaButton, MubinsoInfoBox, MubinsoScreenLayout } from "./MubinsoUI";
import { buildMubinsoLeadMessage } from "./mubinsoLeadMessage";
import {
  FAMILY_TRACK_RECORD,
  mubinsoLeadStatus,
  MubinsoEntry,
  mubinsoEntryTrigger,
} from "./mubinsoConstants";

const TOTAL_STEPS = 3;

/**
 * 무빈소 비용 퍼널: 비용 → 마음 → 장례식장 별 차이 → 전환. (막대만, 숫자 없음)
 *
 * 개편 실험 330 에서 대조군이었고 A 가 이겨 비용 진입의 확정 화면으로 남았다.
 * (이름의 A 는 실험 시절 잔재. 이벤트 연속성 때문에 파일명을 바꾸지 않는다.)
 */
export default function MubinsoCostFunnelA({
  isUrgent,
  situationAnswer,
  topicAnswer,
  onComplete,
  entry,
  family = false,
}: {
  isUrgent: boolean;
  situationAnswer: string;
  topicAnswer: string;
  onComplete: () => void;
  /** 진입 구분. 퍼널별 개별 이벤트({p}-cta_main 등) 발화에 쓴다 */
  entry: MubinsoEntry;
  /** M5 가족장 경로. 고이 실적 문구를 「장례 5,000건」으로 바꾼다(첫 화면과 같은 숫자) */
  family?: boolean;
}) {
  const [step, setStep] = useState(1);
  const [openModal, setOpenModal] = useState<null | "info" | "consult">(null);

  // 단계 전환 시 스크롤 최상단 + 퍼널별 진행 이벤트.
  useEffect(() => {
    window.scrollTo(0, 0);
    customEvent({
      newGtm: { action: "mubinso_funnel_step", properties: { funnel: "cost", step } },
    });
  }, [step]);

  const goNext = () => {
    customEvent({ newGtm: { path: "mubinso_funnel", trigger_and_target: `funnel-next-${step}` } });
    setStep((s) => s + 1);
  };

  if (step === 1) {
    return (
      <MubinsoScreenLayout
        progress={{ current: 1, total: TOTAL_STEPS }}
        footer={<MubinsoCtaButton label="다음" onClick={goNext} />}
      >
        <Box width="100%" className="head2_b black">
          비용은 이 정도예요
        </Box>
        <Spacing gap={14} />
        <Box width="100%" whiteSpace="pre-line" className="body_16_r gray600">
          보편적인 장례보다
          <br />
          <b style={{ color: colors.orange650 }}>약 700만원</b> 적게 들어요.
        </Box>
        <Spacing gap={28} />
        <MubinsoCostChart />
        <Spacing gap={16} />
        <Box width="100%" className="body_14_r gray500">
          빈소·음식·제단꽃이 빠지는 만큼 비용이 줄어요.
        </Box>
      </MubinsoScreenLayout>
    );
  }

  if (step === 2) {
    return (
      <MubinsoScreenLayout
        progress={{ current: 2, total: TOTAL_STEPS }}
        contentBottomGap={56}
        footer={<MubinsoCtaButton label="다음" onClick={goNext} />}
      >
        <Box width="100%" whiteSpace="pre-line" className="head2_b black">
          형식은 간소해져도,
          <br />
          <span style={{ color: colors.orange650 }}>마음의 깊이</span>는 다르지 않아요
        </Box>
        <Spacing gap={14} />
        <Box width="100%" whiteSpace="pre-line" className="body_16_r gray600">
          {"가장 많은 분들이\n걱정하시는 부분이에요."}
        </Box>
        <Spacing gap={32} />
        <MubinsoInfoBox variant="peach" label="그래서 중요한 것" labelColor={colors.orange750}>
          다른 절차가 생략된 만큼, 고인을 뵙는 <b style={{ color: colors.gray900 }}>입관식</b>에
          마음을 담아 잘 진행하면 돼요.
        </MubinsoInfoBox>
      </MubinsoScreenLayout>
    );
  }

  if (step === 3) {
    return (
      <MubinsoScreenLayout
        progress={{ current: 3, total: TOTAL_STEPS }}
        contentBottomGap={56}
        footer={<MubinsoCtaButton label="다음" onClick={goNext} />}
      >
        <Box width="100%" whiteSpace="pre-line" className="head2_b black">
          장례식장 별
          <br />
          차이는 있어요
        </Box>
        <Spacing gap={28} />
        <BaseStack width="100%" bg={colors.orange200} borderRadius="16px" p={20} spacing={10}>
          <Box className="body_16_r gray800">
            무빈소 장례 진행이{" "}
            <b style={{ color: colors.orange750 }}>불가능한 장례식장도 많아요.</b>
          </Box>
          <Box className="body_14_r" color={colors.orange800}>
            무빈소 장례는 장례식장 입장에서 수익이 크게 남지 않는 형태라, 진행이 어려운 곳이
            많거든요. 그래서 가능한 장례식장을 먼저 확인하는 게 첫 단계예요.
          </Box>
        </BaseStack>
        <Spacing gap={16} />
        <MubinsoInfoBox>
          장례식장 별 시설 이용료 차이도 있으니,{" "}
          <b style={{ color: colors.gray900 }}>정확한 비용은 확인이 필요</b>해요.
        </MubinsoInfoBox>
      </MubinsoScreenLayout>
    );
  }

  // step 4 - 전환
  return (
    <>
      <MubinsoConversionScreen
        entry={entry}
        headline={
          <>
            구체적인 비용 및 정보를
            <br />
            고이에서 <span style={{ color: colors.orange650 }}>모두 무료로</span> 알려드릴게요
          </>
        }
        checkItems={[
          { text: "무빈소 가능한 전국 장례식장 정보" },
          { text: "장례식장별 예상 비용 비교" },
          { text: "상황 발생 시 자세한 절차" },
        ]}
        reviewTitle="실제 무빈소 진행 가족분들의 후기"
        reviews={[
          {
            text: "“무빈소 장례지만 깊은 감동을 받았어요. 추가금 없이 진행해 주셨습니다.”",
            meta: "2026. 06. 30 작성 후기 · 김OO 고객님",
          },
          {
            text: "“물품 변경과 선택이 자유롭고, 가심비 좋고 합리적인 가격이라 마음에 쏙 들었어요.”",
            meta: "2026. 05. 31 작성 후기 · 조OO 고객님",
          },
        ]}
        primaryLabel="장례식장별 예상 비용 받아보기"
        onPrimary={() => {
          customEvent({ newGtm: { path: "mubinso_funnel", trigger_and_target: "conv-info_cta" } });
          customEvent({
            newGtm: {
              path: "mubinso_funnel",
              trigger_and_target: mubinsoEntryTrigger(entry, "cta_main"),
            },
          });
          setOpenModal("info");
        }}
        secondaryLabel="무료 상담 신청하기"
        onSecondary={() => {
          customEvent({ newGtm: { path: "mubinso_funnel", trigger_and_target: "conv-cnslt_cta" } });
          setOpenModal("consult");
        }}
      />

      <MubinsoLeadModal
        isOpen={openModal === "info"}
        onClose={() => setOpenModal(null)}
        title="장례식장별 예상 비용 받아보기"
        freeTag
        description={
          family
            ? `${FAMILY_TRACK_RECORD}을 진행하며\n실제 발생했던 비용 정보를 전달드려요.\n비용 절감 방법까지 함께 안내해드려요.`
            : "무빈소 장례 1,000건 이상을 진행하며\n실제 발생했던 비용 정보를 전달드려요.\n비용 절감 방법까지 함께 안내해드려요."
        }
        buttonText="동의하고 자료 받아보기"
        messageBuilder={() =>
          buildMubinsoLeadMessage({
            ctaType: "info",
            situation: situationAnswer,
            topic: topicAnswer,
          })
        }
        leadStatus={mubinsoLeadStatus(situationAnswer)}
        entry={entry}
        onSubmitted={onComplete}
      />
      <MubinsoLeadModal
        isOpen={openModal === "consult"}
        onClose={() => setOpenModal(null)}
        title="무빈소 장례 무료 상담받기"
        description={
          "진행 가능한 장례식장과 예상 비용, 절차를\n빠르게 안내해드려요.\n\n정보만 받아가셔도 괜찮아요.\n준비하시는 데 최대한 도움드릴게요."
        }
        urgentNote={isUrgent}
        buttonText="지금 무료 상담받기"
        messageBuilder={() =>
          buildMubinsoLeadMessage({
            ctaType: "consult",
            situation: situationAnswer,
            topic: topicAnswer,
          })
        }
        leadStatus={mubinsoLeadStatus(situationAnswer)}
        entry={entry}
        onSubmitted={onComplete}
      />
    </>
  );
}
